import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  resetKeys?: readonly unknown[];
}

interface State {
  hasError: boolean;
}

/**
 * Silently catches render errors (e.g. missing context during error recovery)
 * and renders nothing (or an optional fallback) instead of crashing the page.
 */
export class SilentErrorBoundary extends Component<Props, State> {
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private retryAttempted = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.warn("[SilentErrorBoundary]", error.message);

    // Give one automatic retry for transient render failures during recovery.
    if (!this.retryAttempted) {
      this.retryAttempted = true;
      this.clearRetryTimeout();
      this.retryTimeout = setTimeout(() => {
        this.retryTimeout = null;
        this.setState({ hasError: false });
      }, 0);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      this.state.hasError &&
      haveResetKeysChanged(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.retryAttempted = false;
      this.clearRetryTimeout();
      this.setState({ hasError: false });
      return;
    }

    if (prevState.hasError && !this.state.hasError) {
      this.retryAttempted = false;
    }
  }

  componentWillUnmount() {
    this.clearRetryTimeout();
  }

  private clearRetryTimeout() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

function haveResetKeysChanged(
  prevResetKeys: readonly unknown[] | undefined,
  nextResetKeys: readonly unknown[] | undefined,
) {
  if (prevResetKeys === nextResetKeys) {
    return false;
  }

  if (!prevResetKeys || !nextResetKeys) {
    return true;
  }

  if (prevResetKeys.length !== nextResetKeys.length) {
    return true;
  }

  return prevResetKeys.some((value, index) => !Object.is(value, nextResetKeys[index]));
}
